import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Coins, Star, Sparkles, Loader2, Gift } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

/** Format mm:ss-ish countdown from seconds */
function fmt(sec) {
  if (sec <= 0) return 'now';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function SpinWheel({ open, onOpenChange }) {
  const { user, creditStars, adjustCredits, refresh, bumpStats } = useAuth();
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [reel, setReel] = useState([]);   // 24 emojis used for the rolling animation
  const [result, setResult] = useState(null); // { emoji, label, stars, used_free }
  const [tick, setTick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/credits/spin/status');
      setStatus(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { if (open) { setResult(null); load(); } }, [open, load]);

  // Tick once a second to refresh the "next free spin in …" countdown
  useEffect(() => {
    if (!open || !status || status.free_spin_available || status.next_free_in_sec <= 0) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [open, status]);

  const computedEta = status && !status.free_spin_available
    ? Math.max(0, status.next_free_in_sec - tick)
    : 0;

  const startSpin = async () => {
    if (spinning || !status) return;
    // Build the reel: 22 random prize emojis + (we'll patch the final two after the API result)
    const pool = status.prizes || [];
    const rnd = () => pool[Math.floor(Math.random() * pool.length)]?.emoji || '✨';
    setReel(Array.from({ length: 22 }, rnd));
    setResult(null);
    setSpinning(true);

    // Optimistic credit debit so the header balance dips immediately
    // (VIP free spins are free — no debit; daily-free is also free.)
    const willPay = !status.free_spin_available && (status.vip_free_spins_remaining || 0) === 0;
    if (willPay) adjustCredits(-status.spin_cost);

    try {
      const { data } = await api.post('/credits/spin', {});
      // Patch the final reel cell to land on the awarded prize
      const finalEmoji = data.prize.emoji;
      setReel((cur) => {
        const next = [...cur, rnd(), finalEmoji];
        return next;
      });
      // Animation duration ~1.6s — show result then
      await new Promise((r) => setTimeout(r, 1600));
      setResult({ ...data.prize, used_free: data.used_free, used_vip_free: data.used_vip_free });
      // Authoritative refresh — backend sets exact balances
      creditStars(data.prize.stars);
      bumpStats({ xp: 25 }); // +25 XP per spin (mirror server)
      if (willPay) {
        // adjustCredits already debited optimistically; if backend balance differs we just refresh
      }
      refresh?.();
      load();
    } catch (e) {
      // Roll back optimistic debit
      if (willPay) adjustCredits(status.spin_cost);
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Spin failed — try again');
    } finally {
      setSpinning(false);
    }
  };

  const vipFree = status?.vip_free_spins_remaining || 0;
  const canFree = status?.free_spin_available;
  const canPay = !canFree && (user?.credits || 0) >= (status?.spin_cost || 0);
  const ctaLabel = vipFree > 0
    ? `Spin free · VIP (${vipFree} left)`
    : canFree
      ? 'Spin free!'
      : `Spin for ${status?.spin_cost || 100} credits`;
  const ctaDisabled = spinning || !status || (vipFree === 0 && !canFree && !canPay);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md" data-testid="spin-wheel-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkles size={18} className="text-amber-300" /> Daily Stardust Spin
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Spend your Credits for a chance at extra Stars. One free spin every 24 hours.
          </DialogDescription>
        </DialogHeader>

        {/* Reel display */}
        <div className="rounded-2xl border border-white/10 bg-[#0F0F18] overflow-hidden">
          <div className="relative h-24 flex items-center justify-center" data-testid="spin-reel">
            {result && !spinning ? (
              <div className="flex flex-col items-center" data-testid="spin-result">
                <div className="text-5xl drop-shadow-[0_0_20px_rgba(251,191,36,0.6)] animate-pulse">{result.emoji}</div>
                <div className="text-[11px] uppercase tracking-wider text-white/40 mt-1">{result.label}</div>
              </div>
            ) : spinning ? (
              <div className="flex gap-3 text-5xl spin-reel-anim">
                {reel.map((e, i) => <span key={i}>{e}</span>)}
              </div>
            ) : (
              <div className="text-5xl opacity-60">🎰</div>
            )}
            {/* Center indicator line */}
            <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px bg-gradient-to-b from-transparent via-amber-300/40 to-transparent" />
          </div>
          {result && !spinning && (
            <div className="border-t border-white/10 py-3 text-center" data-testid="spin-award">
              <div className="text-xs text-white/50 uppercase tracking-wider">You won</div>
              <div className="font-display font-bold text-2xl text-amber-300 flex items-center justify-center gap-1.5 mt-0.5">
                <Star size={18} fill="currentColor" />
                +{result.stars.toLocaleString()}
              </div>
              {result.used_vip_free && <div className="text-[11px] text-amber-300 mt-1">VIP free spin used 👑</div>}
              {result.used_free && !result.used_vip_free && <div className="text-[11px] text-emerald-300 mt-1">Free daily spin used ✨</div>}
            </div>
          )}
        </div>

        {/* Balance + CTA */}
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-white/60 flex items-center gap-1.5" data-testid="spin-balance">
            <Coins size={14} className="text-cyan-300" />
            <span className="text-cyan-300 font-semibold">{(user?.credits ?? 0).toLocaleString()}</span>
            <span className="text-white/40">credits</span>
          </div>
          {!canFree && (
            <div className="text-[11px] text-white/40" data-testid="next-free-eta">
              Next free spin in <span className="text-white/70">{fmt(computedEta)}</span>
            </div>
          )}
        </div>

        <Button
          onClick={startSpin}
          disabled={ctaDisabled}
          data-testid="spin-cta"
          className={`w-full mt-1 font-semibold border-0 ${canFree
            ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-black hover:from-amber-500 hover:to-orange-600'
            : 'bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white'} disabled:opacity-40`}
        >
          {spinning ? <Loader2 className="animate-spin mr-2" size={16} /> : <Gift size={16} className="mr-2" />}
          {spinning ? 'Spinning…' : ctaLabel}
        </Button>

        {/* Prize pool legend */}
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5">Possible prizes</div>
          <div className="grid grid-cols-3 gap-1.5">
            {(status?.prizes || []).map((p) => (
              <div key={p.id} className="rounded-xl px-2 py-1.5 bg-white/3 border border-white/5 flex items-center gap-1.5" data-testid={`prize-${p.id}`}>
                <span className="text-base">{p.emoji}</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold truncate">{p.label}</div>
                  <div className="text-[9px] text-amber-300 flex items-center gap-0.5">
                    <Star size={7} fill="currentColor" />
                    {p.min === p.max ? p.min.toLocaleString() : `${p.min.toLocaleString()}–${p.max.toLocaleString()}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent wins */}
        {status?.recent?.length > 0 && (
          <div className="mt-2">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Your recent spins</div>
            <div className="space-y-0.5">
              {status.recent.slice(0, 3).map((r) => (
                <div key={r.id} className="flex items-center justify-between text-[11px] text-white/60">
                  <span className="flex items-center gap-1.5"><span className="text-sm">{r.prize_emoji}</span> {r.prize_label}</span>
                  <span className="text-amber-300 flex items-center gap-0.5"><Star size={8} fill="currentColor" />+{r.stars_awarded.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tiny inline CSS for the reel animation — kept here so the component is self-contained */}
        <style>{`
          @keyframes wv-spin-reel {
            0%   { transform: translateX(0); }
            100% { transform: translateX(calc(-1 * (var(--reel-len, 22) * 56px - 60px))); }
          }
          .spin-reel-anim {
            animation: wv-spin-reel 1.6s cubic-bezier(0.18, 0.7, 0.2, 1) forwards;
            will-change: transform;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
